import { useEffect } from "react";
import { getAuth } from "firebase/auth";
import { firebaseApp } from "../libs/firebase";
import { useLoginState } from "../studioxr/auth/LoginState";
import { useFeatureFlagStore } from "./useFeatureFlagStore";
import { identifyUser } from "../libs/analyticsManager";

/**
 * Loads the current user's feature-flag verdicts once their profile resolves,
 * and resets on logout. Triggered off the LoginState profile (not just the auth
 * session) so the backend is guaranteed to find a written profile when it
 * evaluates region / pro / created-at targeting.
 *
 * Side-effect only — verdicts live in the global useFeatureFlagStore; consume
 * them anywhere via useFeatureFlag("flag_key").
 */
export function FeatureFlagsProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  const uid = useLoginState((s) => s.profile?.uid ?? null);
  const loadFlags = useFeatureFlagStore((s) => s.loadFlags);
  const reset = useFeatureFlagStore((s) => s.reset);
  const loadedUid = useFeatureFlagStore((s) => s.loadedUid);

  useEffect(() => {
    if (uid) {
      void loadFlags(uid);
    } else {
      reset();
    }
  }, [uid, loadFlags, reset]);

  // Re-identify once verdicts load so the `ff_*` traits reach the analytics
  // tools (the login-time identify usually fires before verdicts arrive).
  useEffect(() => {
    if (!uid || loadedUid !== uid) return;
    const currentUser = getAuth(firebaseApp).currentUser;
    const profile = useLoginState.getState().profile;
    if (currentUser && profile) identifyUser({ currentUser, profile });
  }, [loadedUid, uid]);

  return <>{children}</>;
}
